import {useAcceptSubmissionMutation} from "@/api/submissionApi";
import Button from "@/components/Common/Button";
import type {TSubmissionStatus} from "@/hooks/useContentApproval";
import {useCarouselStore} from "@/store/carousel";
import {rejectContentModalAtom, reviewContentModalAtom} from "@/store/jotaiAtoms";
import type {TCarouselUser} from "@/utils/campaignUtils";
import dayjs from "dayjs";
import {useSetAtom} from "jotai";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const ContentApprovalCarouselFooter: FC<{
  status: TSubmissionStatus;
  date?: string;
  id?: number;
  isRevisionAvailable?: boolean;
  user: TCarouselUser;
}> = ({status, date, id, isRevisionAvailable, user}) => {
  const {t} = useTranslation(["content_approval", "common"]);
  const closeCarousel = useCarouselStore(state => state.reset);
  const setReviewContentModalData = useSetAtom(reviewContentModalAtom);
  const setRejectContentModalData = useSetAtom(rejectContentModalAtom);

  const {mutate: approveContent, isPending: isApprovePending} = useAcceptSubmissionMutation({
    id: id!,
    onSuccess: closeCarousel,
  });

  return (
    <div
      onClick={e => e.stopPropagation()}
      className="border-neutral-black mt-2 flex min-h-17 w-full flex-col items-center gap-5 self-center rounded-lg border bg-black px-4 py-2.5 md:flex-row">
      {(status === "APPROVED" || status === "REJECTED" || status === "REVISION_REQUESTED") && (
        <span className="text-sm text-white">
          {t(
            status === "APPROVED"
              ? "waiting_to_post"
              : status === "REJECTED"
                ? "content_rejected"
                : "waiting_for_influencer_to_revise",
          )}
        </span>
      )}
      {status === "PENDING" && id && (
        <div className="flex w-full flex-col items-center gap-1 md:flex-row">
          {isRevisionAvailable ? (
            <Button
              buttonStyle="White"
              className="h-12 w-full px-8 font-bold md:w-fit"
              onClick={() => {
                closeCarousel();

                setReviewContentModalData(state => {
                  state.isOpen = true;
                  state.isRevisionFormOpen = true;
                  state.showRevisionFormClosingButton = false;
                  state.submissionId = id;
                });
              }}>
              {t("common:revise")}
            </Button>
          ) : (
            <Button
              buttonStyle="White"
              className="h-12 w-full px-8 font-bold md:w-fit"
              onClick={() => {
                closeCarousel();
                setRejectContentModalData(state => {
                  state.isOpen = true;
                  state.submissionId = id;
                  state.user = user;
                });
              }}>
              <span className="text-danger-medium-red"> {t("reject")}</span>
            </Button>
          )}
          <Button
            buttonStyle="Blue"
            className="h-12 w-full font-bold"
            isLoading={isApprovePending}
            onClick={() => approveContent()}>
            {t("common:approve")}
          </Button>
        </div>
      )}
      {status === "PENDING" && date && (
        <span className="font-bold text-white">{` ${t("review_by")} ${dayjs(date).format("DD.MM.YY")}`}</span>
      )}
    </div>
  );
};

export default ContentApprovalCarouselFooter;
